import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  Switch,
  Typography,
  Space,
  Button,
  message,
  Alert,
} from "antd";
import { CopyOutlined } from "@ant-design/icons";
import { IconUploader } from "./IconUploader";

const { Text } = Typography;
const { TextArea } = Input;

const PLATFORMS_OPTIONS = [
  { value: "quest", label: "Quest (Android APK)" },
  { value: "webgl", label: "WebGL desktop" },
  { value: "standalone", label: "Standalone (Win/Mac/Linux)" },
  { value: "aframe", label: "A-Frame (compat futura)" },
];

/**
 * Modal de edicion de un LiveStreamGame.
 *
 * El gameKey NO se edita: se muestra como referencia readonly con boton
 * "Copiar" para que el admin pueda re-enviarselo a alguien.
 *
 * Props:
 *   - open: bool
 *   - game: object | null (si null, no renderiza)
 *   - onClose()
 *   - onSave(patch) -> Promise<game>
 *   - onUploadIcon(gameKey, file) -> Promise<publicUrl>
 */
export function LiveGameEditModal({
  open,
  game,
  onClose,
  onSave,
  onUploadIcon,
}) {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [iconState, setIconState] = useState(null);

  useEffect(() => {
    if (game && open) {
      form.setFieldsValue({
        label: game.label,
        description: game.description || "",
        platforms: game.platforms || ["quest"],
        enabled: game.enabled,
      });
      setIconState(game.iconUrl ? { url: game.iconUrl } : null);
    }
  }, [game, open, form]);

  if (!game) return null;

  const copyKey = () => {
    navigator.clipboard.writeText(game.gameKey);
    message.success("gameKey copiado");
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      const patch = {
        label: values.label.trim(),
        description: values.description?.trim() || "",
        platforms: values.platforms,
        enabled: values.enabled,
      };

      // Upload icon nuevo si lo hay.
      if (iconState && iconState.file) {
        try {
          const publicUrl = await onUploadIcon(game.gameKey, iconState.file);
          patch.iconUrl = publicUrl;
        } catch (e) {
          message.warning("La imagen no se pudo subir, el resto si se guarda.");
        }
      } else if (iconState === null && game.iconUrl) {
        // El admin quito la imagen explicitamente.
        patch.iconUrl = null;
      }

      await onSave(patch);
      onClose();
    } catch (e) {
      if (e?.errorFields) return; // antd validation error
      message.error(e?.message || "Error guardando");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={`Editar: ${game.label}`}
      width={620}
      onOk={handleSave}
      okText="Guardar cambios"
      cancelText="Cancelar"
      confirmLoading={submitting}
      destroyOnClose
    >
      <Alert
        type="info"
        message={
          <Space size={8}>
            <Text strong>gameKey:</Text>
            <Text code>{game.gameKey}</Text>
            <Button
              size="small"
              type="text"
              icon={<CopyOutlined />}
              onClick={copyKey}
            />
          </Space>
        }
        description="El gameKey es immutable. Si necesitas cambiarlo, crea un juego nuevo y desactiva este."
        style={{ marginBottom: 16 }}
      />

      <Form form={form} layout="vertical">
        <Form.Item
          name="label"
          label="Nombre"
          rules={[
            { required: true, message: "Nombre requerido" },
            { min: 3, max: 80 },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="description"
          label="Descripcion"
          rules={[{ max: 500 }]}
        >
          <TextArea rows={3} />
        </Form.Item>
        <Form.Item
          name="platforms"
          label="Plataformas"
          rules={[{ required: true, message: "Selecciona al menos una" }]}
        >
          <Select mode="multiple" options={PLATFORMS_OPTIONS} />
        </Form.Item>
        <Form.Item label="Icono">
          <IconUploader value={iconState} onChange={setIconState} />
        </Form.Item>
        <Form.Item name="enabled" label="Activo" valuePropName="checked">
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  );
}
